"use client";

import type { CSSProperties, ReactElement, ReactNode } from "react";
import {
  AttachResultShape,
  type AttachResult,
  type ParsedAttachResults,
} from "@cabal/shared/domain/attached-result/index.js";
import {
  CRC_ACCENT,
  CRC_DIVIDER,
  CRC_PLATE_BACKGROUND_COLOR,
  CRC_R_CARD,
  CRC_TEXT,
  CRC_TEXT_FAINT,
  CRC_TEXT_MUTED,
} from "@/components/ui/Cresultcard/cresultcard.tokens";
import {
  CATTACHEDRESULT_PLATE_STYLE,
  CATTACHEDRESULT_TILE_STYLE,
} from "./cattachedresult.plate";

const ROOT_STYLE: CSSProperties = CATTACHEDRESULT_PLATE_STYLE;

const RESULT_STYLE: CSSProperties = CATTACHEDRESULT_TILE_STYLE;

const SHAPE_FIELD = "shape";
/** The common per-result metadata property. Never a fact row: it is the block
 * writer's own comment on the item, rendered as the card's comment badge. */
const WRITER_LABEL_FIELD = "writerLabel";

const WRITER_LABEL_STYLE: CSSProperties = {
  display: "inline-block",
  margin: "0 0 7px",
  padding: "2px 7px",
  border: `1px solid ${CRC_DIVIDER}`,
  borderRadius: CRC_R_CARD,
  backgroundColor: CRC_PLATE_BACKGROUND_COLOR,
  color: CRC_ACCENT,
  fontSize: 9.5,
  fontStyle: "italic",
  fontWeight: 650,
  lineHeight: 1.4,
  overflowWrap: "anywhere",
};

interface AttachedFact {
  readonly key: string;
  readonly value: string;
}

function readableKey(value: string): string {
  return value
    .replace(/\[(\d+)\]/g, " $1")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._]/g, " ")
    .trim()
    .toLowerCase();
}

function factsFromValue(input: {
  key: string;
  value: unknown;
}): AttachedFact[] {
  const { key, value } = input;
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap((entry, index) =>
      factsFromValue({ key: `${key}[${String(index + 1)}]`, value: entry }),
    );
  }
  if (typeof value === "object") {
    return Object.entries(value).flatMap(([childKey, childValue]) =>
      factsFromValue({ key: `${key}.${childKey}`, value: childValue }),
    );
  }
  return [{ key: readableKey(key), value: String(value) }];
}

function factsOf(result: AttachResult): AttachedFact[] {
  if (result.shape === AttachResultShape.Generic) {
    return result.fields.map((field) => ({
      key: field.key,
      value: field.value,
    }));
  }
  if ("values" in result) {
    const grouped = result.values as Readonly<
      Record<string, readonly string[] | undefined>
    >;
    return Object.entries(grouped).flatMap(([key, values]) =>
      (values ?? []).map((value) => ({ key, value })),
    );
  }
  return Object.entries(result).flatMap(([key, value]) =>
    key === SHAPE_FIELD || key === WRITER_LABEL_FIELD
      ? []
      : factsFromValue({ key, value }),
  );
}

function titleOf(result: AttachResult): string {
  if ("headline" in result) return result.headline;
  if (result.shape === AttachResultShape.WebSearch) return result.title;
  if (
    result.shape === AttachResultShape.Hotels ||
    result.shape === AttachResultShape.Workouts ||
    result.shape === AttachResultShape.Nutrition
  ) {
    return result.name;
  }
  return `${result.tripType} · ${result.currency} ${String(result.priceAmount)}`;
}

function renderValue(value: string): ReactNode {
  if (!/^https?:\/\//i.test(value)) return value;
  return (
    <a
      href={value}
      rel="noreferrer"
      style={{ color: CRC_ACCENT }}
      target="_blank"
    >
      {value}
    </a>
  );
}

function AttachedResultItem(input: { result: AttachResult }): ReactElement {
  const facts = factsOf(input.result);
  return (
    <article
      style={RESULT_STYLE}
    >
      {input.result.writerLabel != null && (
        <p
          style={WRITER_LABEL_STYLE}
        >
          {input.result.writerLabel}
        </p>
      )}
      <h4
        style={{
          margin: 0,
          color: CRC_TEXT,
          fontSize: 13,
          fontWeight: 700,
          lineHeight: 1.35,
        }}
      >
        {titleOf(input.result)}
      </h4>
      <dl style={{ display: "grid", gap: 0, margin: "10px 0 0" }}>
        {facts.map((fact, index) => (
          <div
            key={`${fact.key}-${String(index)}`}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(92px, 0.34fr) minmax(0, 1fr)",
              gap: 12,
              paddingBlock: 6,
              borderTop: `1px solid ${CRC_DIVIDER}`,
            }}
          >
            <dt
              style={{
                color: CRC_TEXT_FAINT,
                fontSize: 9.5,
                fontWeight: 700,
                lineHeight: 1.4,
                overflowWrap: "anywhere",
              }}
            >
              {fact.key}
            </dt>
            <dd
              style={{
                minWidth: 0,
                margin: 0,
                color: CRC_TEXT_MUTED,
                fontSize: 10.5,
                fontWeight: 540,
                lineHeight: 1.45,
                overflowWrap: "anywhere",
              }}
            >
              {renderValue(fact.value)}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
}

export interface CattachedresultParsedItemsProps {
  readonly block: ParsedAttachResults;
}

/** The lossless shared-parser render. Every parsed field is drawn; absent
 * values stay absent, repeated lines stay repeated, and no display string is
 * re-parsed into a stronger type than the persisted format provides. */
export function CattachedresultParsedItems({
  block,
}: CattachedresultParsedItemsProps): ReactElement {
  const dropped =
    block.droppedItemCount +
    block.droppedFieldCount +
    block.unknownDirectiveCount;
  return (
    <section
      aria-label={block.about ?? "Attached results"}
      style={ROOT_STYLE}
    >
      <header style={{ padding: "8px 9px 3px" }}>
        <div
          style={{
            color: CRC_ACCENT,
            fontSize: 9,
            fontWeight: 750,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          {block.shape} · {String(block.results.length)} results
        </div>
        {block.about != null && (
          <h3
            style={{
              margin: "5px 0 0",
              color: CRC_TEXT,
              fontSize: 14,
              lineHeight: 1.35,
            }}
          >
            {block.about}
          </h3>
        )}
        {block.capturedAtLabel != null && (
          <p
            style={{ margin: "4px 0 0", color: CRC_TEXT_FAINT, fontSize: 9.5 }}
          >
            {block.capturedAtLabel}
          </p>
        )}
        {dropped > 0 && (
          <p
            style={{ margin: "4px 0 0", color: CRC_TEXT_MUTED, fontSize: 9.5 }}
          >
            {String(block.droppedItemCount)} items,{" "}
            {String(block.droppedFieldCount)}
            {" fields and "}
            {String(block.unknownDirectiveCount)} directives were rejected by
            the parser ceilings.
          </p>
        )}
      </header>
      {block.results.map((result, index) => (
        <AttachedResultItem
          key={`${result.shape}-${String(index)}`}
          result={result}
        />
      ))}
    </section>
  );
}
